import { memo, useMemo } from 'react';
import { icons } from 'lucide-react';

/**
 * Normalize any icon value to an IconObject.
 * Handles: IconObject, legacy emoji string, null/undefined.
 */
export function normalizeIcon(icon, fallback = '📁') {
    if (!icon) return { type: 'emoji', value: fallback, color: null };
    if (typeof icon === 'string') return { type: 'emoji', value: icon || fallback, color: null };
    if (icon.type && icon.value) return icon;
    return { type: 'emoji', value: fallback, color: null };
}

/**
 * IconRenderer — Universal display component for the IconObject schema.
 *
 * Supports:
 *   - { type: "emoji",  value: "📁" }
 *   - { type: "lucide", value: "Folder", color: "#8B5CF6" }
 *   - { type: "upload", value: "https://..." }
 *   - Legacy plain string (raw emoji character) — auto-detected
 *
 * Props:
 *   icon      IconObject | string | null
 *   size      number (px, default: 20)
 *   className string
 *   fallback  string (default emoji if icon is empty)
 */
function IconRenderer({ icon, size = 20, className = '', fallback = '📁' }) {
    const normalized = useMemo(() => normalizeIcon(icon, fallback), [icon, fallback]);

    if (normalized.type === 'lucide') {
        const LucideIcon = icons[normalized.value];
        if (!LucideIcon) {
            // Fallback to emoji if icon name not found
            return (
                <span
                    className={`icon-renderer icon-renderer--emoji ${className}`}
                    style={{ fontSize: size, lineHeight: 'var(--leading-none)', width: size, height: size, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                    role="img"
                >
                    {fallback}
                </span>
            );
        }
        return (
            <span
                className={`icon-renderer icon-renderer--lucide ${className}`}
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: size, height: size }}
            >
                <LucideIcon size={size * 0.85} color={normalized.color || 'currentColor'} strokeWidth={2} />
            </span>
        );
    }

    if (normalized.type === 'upload') {
        return (
            <img
                src={normalized.value}
                alt=""
                className={`icon-renderer icon-renderer--upload ${className}`}
                style={{ width: size, height: size, borderRadius: 'var(--radius-sm)', objectFit: 'cover' }}
            />
        );
    }

    // Default: emoji
    return (
        <span
            className={`icon-renderer icon-renderer--emoji ${className}`}
            style={{ fontSize: size * 0.85, lineHeight: 'var(--leading-none)', width: size, height: size, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
            role="img"
        >
            {normalized.value}
        </span>
    );
}

export default memo(IconRenderer);
